import React from 'react'
import { useGetBusiness } from '@marketing-milk/frontend'
import {
  AdCreative,
  CampaignSchedule,
  PublishCampaignDTO,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { Button, Checkbox, Drawer, FormControlLabel, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  CampaignAdsetsStep,
  CampaignAdStep,
  CampaignSettingsStep,
} from 'pages/business/campaigns/forms/steps'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { campaignService } from '../../../../../app/services'

interface Props {
  isOpen: boolean
  onClose: () => void
  type?: 'audiences' | 'settings' | 'creatives'
  updateDto: (dto: PublishCampaignDTO) => void
  audiences?: SavedAudience[]
  sprintCreatives?: AdCreative[]
  /**
   * Sprint creatives a sibling draft has already claimed. Hidden by default, because the common
   * motion is partitioning a sprint's ads across its drafts and Select All should mean "the ones
   * nobody took" — but shown on request rather than filtered away silently, since an ad may
   * legitimately run in two of a sprint's campaigns. The checkbox's count is what says they exist.
   */
  creativeIDsUsedByOtherDrafts?: Set<number>
  initialDto: PublishCampaignDTO
  /** The sprint's review surface, passed through to the ad picker. */
  reviewHref?: string
}

const editorLabels: Record<NonNullable<Props['type']>, string> = {
  settings: 'Campaign settings',
  audiences: 'Audiences',
  creatives: 'Ads',
}

const sortCreativesForDraft = (
  sprintCreatives: AdCreative[] | undefined,
  dto: PublishCampaignDTO
): AdCreative[] | undefined => {
  if (!sprintCreatives) return undefined
  const assignedIDs = new Set(dto.creativeGroups.flat().filter((id): id is number => id !== null))
  return [
    ...sprintCreatives.filter(c => assignedIDs.has(c.id)),
    ...sprintCreatives.filter(c => !assignedIDs.has(c.id)),
  ]
}

export const EditDraftCampaignModal = ({
  audiences,
  sprintCreatives,
  creativeIDsUsedByOtherDrafts,
  ...props
}: Props) => {
  const [campaignDto, setCampaignDto] = useState<PublishCampaignDTO | undefined>(props.initialDto)
  const [includeOtherCampaignAds, setIncludeOtherCampaignAds] = useState(false)
  const usedElsewhereCount = (sprintCreatives ?? []).filter(c =>
    creativeIDsUsedByOtherDrafts?.has(c.id)
  ).length
  const sortedCreatives = useMemo(() => {
    const visible =
      includeOtherCampaignAds || !creativeIDsUsedByOtherDrafts
        ? sprintCreatives
        : sprintCreatives?.filter(c => !creativeIDsUsedByOtherDrafts.has(c.id))
    return sortCreativesForDraft(visible, props.initialDto)
  }, [sprintCreatives, creativeIDsUsedByOtherDrafts, includeOtherCampaignAds, props.initialDto])
  const [schedule, setCampaignSchedule] = useState<CampaignSchedule>()
  const business = useGetBusiness()

  const onUpdate = () => {
    if (!campaignDto) return
    props.updateDto(campaignDto)
    toast.warn('This may take a few moments to reflect in the U.I.')
    props.onClose()
  }

  const fetchCampaignSchedule = async (numOfCreativeGroups: number) => {
    if (campaignDto && numOfCreativeGroups > 1) {
      const generatedSchedule = await campaignService.getCampaignSchedule(business.id, {
        startTime: campaignDto.campaign.startTime,
        endTime: campaignDto.campaign.endTime,
        numOfAdGroups: numOfCreativeGroups,
      })
      setCampaignSchedule(generatedSchedule)
    }
  }

  useEffect(() => {
    if (!campaignDto) return
    fetchCampaignSchedule(campaignDto.creativeGroups.length)
  }, [campaignDto?.campaign, campaignDto?.creativeGroups])

  return (
    <Drawer anchor="right" open={props.isOpen} onClose={props.onClose}>
      {campaignDto && (
        <div style={{ width: '80vw' }} className="flex h-full flex-col max-w-full overflow-hidden">
          {/* The panel is the only thing naming what is being edited — the steps inside it are
              shared with the /campaigns wizard, where the step rail does that job. */}
          <div className="flex items-center gap-3 border-b border-mm-neutral-1-200 bg-white px-6 py-2">
            <h2 className="min-w-0 flex-1 truncate text-base font-bold text-mm-neutral-1-900">
              {campaignDto.campaign.name}
            </h2>
            <span className="flex-shrink-0 text-sm text-mm-neutral-1-500">
              {editorLabels[props.type ?? 'settings']}
            </span>
            <IconButton onClick={props.onClose} aria-label="Close" size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="overflow-y-auto flex-grow overflow-x-hidden bg-mm-neutral-2-50">
            {props.type === 'audiences' && (
              <CampaignAdsetsStep
                // Consumed only by the wizard's step rail; the panel header names the campaign here.
                title="Audiences"
                audiences={audiences}
                campaignDto={campaignDto}
                setCampaignDto={setCampaignDto}
              />
            )}

            {props.type === 'creatives' && (
              <CampaignAdStep
                schedule={schedule}
                updateSchedule={fetchCampaignSchedule}
                campaignDto={campaignDto}
                setCampaignDto={setCampaignDto}
                onSelectedCreativesChange={() => {}}
                limitToCreatives={sortedCreatives}
                reviewHref={props.reviewHref}
                listHeader={
                  usedElsewhereCount > 0 ? (
                    <FormControlLabel
                      // Boxed like the controls either side of it, and tinted when on, so it
                      // reads as part of the toolbar rather than floating between buttons.
                      className={`m-0 rounded-md border py-1 pl-1 pr-3 transition-colors ${
                        includeOtherCampaignAds
                          ? 'border-mm-primary-600 bg-mm-primary-50'
                          : 'border-mm-neutral-1-200 bg-white'
                      }`}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={includeOtherCampaignAds}
                          onChange={(_, checked) => setIncludeOtherCampaignAds(checked)}
                        />
                      }
                      // The count carries the meaning: unchecked with a silent empty list is the
                      // bug this replaced, where "no ads here" read as "you have no creatives".
                      label={
                        <span className="whitespace-nowrap text-sm font-semibold text-mm-neutral-1-700">
                          {`Include ${usedElsewhereCount} ${
                            usedElsewhereCount === 1 ? 'ad' : 'ads'
                          } on other campaigns`}
                        </span>
                      }
                    />
                  ) : undefined
                }
              />
            )}

            {props.type === 'settings' && (
              <CampaignSettingsStep
                isEdit={true}
                // drafts have never been published, so the start date stays editable
                isPublished={false}
                campaignDto={campaignDto}
                setCampaignDto={setCampaignDto}
              />
            )}
          </div>

          {/* Sticky rather than at the end of the content: Settings scrolls past a two-month
              calendar, so the save used to sit below everything it saves. */}
          <div className="flex items-center justify-end gap-3 border-t border-mm-neutral-1-200 bg-white px-6 py-3">
            <Button onClick={props.onClose} color="inherit">
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={onUpdate}>
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  )
}
